import { useCallback, useEffect, useRef, useState } from "react";

export function useVoices() {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  useEffect(() => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const refresh = () => setVoices(window.speechSynthesis.getVoices());
    refresh();
    window.speechSynthesis.onvoiceschanged = refresh;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);
  return voices;
}

export function useSpeech() {
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const cancelledRef = useRef(false);

  const stop = useCallback(() => {
    if (typeof window === "undefined") return;
    cancelledRef.current = true;
    window.speechSynthesis.cancel();
  }, []);

  const speak = useCallback(
    ({
      text,
      rate,
      voice,
      onEnd,
      onError,
    }: {
      text: string;
      rate: number;
      voice: SpeechSynthesisVoice | null;
      onEnd: () => void;
      onError?: () => void;
    }) => {
      if (typeof window === "undefined" || !("speechSynthesis" in window)) {
        return;
      }
      cancelledRef.current = false;
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = rate;
      if (voice) utterance.voice = voice;
      utterance.onend = () => {
        if (!cancelledRef.current) onEnd();
      };
      utterance.onerror = () => {
        if (!cancelledRef.current) onError?.();
      };
      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    },
    [],
  );

  useEffect(
    () => () => {
      if (typeof window !== "undefined") window.speechSynthesis.cancel();
    },
    [],
  );

  return { speak, stop };
}
